import type { ReactNode } from "react";

import { InboxOutlined } from "@ant-design/icons";
import { Typography } from "antd";

export interface EmptyStateProps {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  size?: "default" | "large";
}

/**
 * Unified empty-state placeholder for tables, lists, drawers, etc.
 *
 * Visual spec (see Phase 7 design notes):
 * - vertical centered stack inside a soft cream card (#FAF6F0, radius 12)
 * - default padding 48, large padding 96
 * - icon defaults to InboxOutlined 64px in antd secondary text color
 * - title h4 / weight 600, description muted, max-width 400 centered
 */
export function EmptyState({
  title = "暂无数据",
  description,
  icon,
  action,
  size = "default",
}: EmptyStateProps) {
  const padding = size === "large" ? 96 : 48;
  const resolvedIcon =
    icon ?? (
      <InboxOutlined
        style={{ fontSize: 64, color: "rgba(0, 0, 0, 0.45)" }}
      />
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding,
        background: "#FAF6F0",
        borderRadius: 12,
      }}
    >
      <div style={{ marginBottom: 16 }}>{resolvedIcon}</div>
      <Typography.Title level={4} style={{ margin: 0, fontWeight: 600 }}>
        {title}
      </Typography.Title>
      {description && (
        <Typography.Paragraph
          type="secondary"
          style={{
            marginTop: 8,
            marginBottom: 0,
            maxWidth: 400,
          }}
        >
          {description}
        </Typography.Paragraph>
      )}
      {action && <div style={{ marginTop: 24 }}>{action}</div>}
    </div>
  );
}
